import { assign } from 'xstate';
import { GetExistingLeaseDocumentsResponse, LeaseDocuments } from '../../gql/graphql';
import { getCurrentLanguage, SupportedLanguage, toSupportedLanguage } from '../../helpers/language/language.helper';

/**
 * Shared by the LeaseAgreement sub-machines in the long form, short form and virtual card flows.
 *
 * The render language is not a GraphQL argument: Apollo puts the currently-selected UI language
 * on the X-Language header, read at call time (src/apollo/index.ts), and the backend picks the
 * matching template. Re-invoking generateContract after a toggle is all it takes.
 */

type LeaseDocumentsByLanguage = Partial<Record<SupportedLanguage, LeaseDocuments>>;

/**
 * The cache is dropped on every entry rather than kept for the session: re-entering the page
 * means the lease terms may have been re-selected (e.g. EDIT_CART), which would make an earlier
 * render stale. Toggling language moves between child states, so it does not re-run this.
 */
export const AssignInitialLeaseDocumentLanguage = assign((_context: any, _event: any) => ({
  leaseDocumentsLanguage: getCurrentLanguage(),
  leaseDocumentsByLanguage: {} as LeaseDocumentsByLanguage,
  requestedLeaseDocumentsLanguage: getCurrentLanguage(),
}));

export const AssignGeneratedLeaseDocuments = assign((context: any, event: any) => {
  const leaseDocuments: LeaseDocuments | undefined = event.data?.leaseDocuments;
  // Files under the language the request went out in, not the one on screen now, so a toggle
  // landing mid-flight cannot mislabel the response.
  const language: SupportedLanguage = context.leaseDocumentsLanguage ?? getCurrentLanguage();

  return {
    leaseDocuments,
    leaseDocumentsByLanguage: {
      ...context.leaseDocumentsByLanguage,
      [language]: leaseDocuments,
    } as LeaseDocumentsByLanguage,
  };
});

/**
 * Read-only counterpart to AssignGeneratedLeaseDocuments: seeds the same cache, but from a single
 * getExistingLeaseDocuments response instead of one generate-on-demand call per language. The
 * signed render and its English companion (if any) both land in the cache in one shot.
 *
 * The document opens in the language it was signed in, whatever the footer shows (OR-2725). The
 * signed render is the record, and the dealer's Spanish flag may be off by the time it is viewed,
 * which forces the app to English and hides the footer toggle; opening in the app language would
 * then leave only the unsigned companion on screen. From there the footer toggle moves the
 * document only when a companion is on file (OR-3375), and LeaseAgreement forwards only a toggle
 * made on the page, never the language it happened to open with. Both languages are cached then,
 * so a read-only visit never falls through to generation.
 */
export const AssignExistingLeaseDocuments = assign((context: any, event: any) => {
  const response: GetExistingLeaseDocumentsResponse | undefined = event.data;
  const signedLanguage = toSupportedLanguage(response?.signedLanguageCode);
  const leaseDocuments: LeaseDocuments = {
    __typename: 'LeaseDocuments',
    leaseAgreement: response?.leaseAgreement!,
    welcomeLetter: response?.welcomeLetter!,
  };

  const leaseDocumentsByLanguage: LeaseDocumentsByLanguage = {
    ...context.leaseDocumentsByLanguage,
    [signedLanguage]: leaseDocuments,
  };
  // AES has no separate "has companion" flag — presence is the field itself being non-empty
  // (see application-edge-service's GetExistingLeaseDocuments resolver). The companion is always
  // the unsigned English render of a non-English signing (OR-2723); it never replaces the signed
  // render itself — guarded on signedLanguage below rather than trusted from the response alone,
  // because an English-signed lease that somehow carried a companion would otherwise overwrite the
  // signed English render just filed above, leaving the cache with no Spanish entry to match the
  // toggle that presence would turn on, and send that toggle to generateContract instead.
  const hasEnglishCompanion = signedLanguage !== 'en' && !!response?.leaseAgreementEnglishCompanion;
  if (hasEnglishCompanion) {
    // Only the lease agreement has a persisted English companion (OR-2723). The welcome letter
    // does not until OR-3379 lands, so toggling to English still shows it in Spanish.
    leaseDocumentsByLanguage.en = {
      __typename: 'LeaseDocuments',
      leaseAgreement: response!.leaseAgreementEnglishCompanion!,
      welcomeLetter: response?.welcomeLetter!,
    };
  }

  return {
    leaseDocuments,
    leaseDocumentsLanguage: signedLanguage,
    requestedLeaseDocumentsLanguage: signedLanguage,
    leaseDocumentsByLanguage,
    hasEnglishLeaseCompanion: hasEnglishCompanion,
  };
});

/**
 * Records the customer's latest language choice without changing the active child state.
 * The LeaseAgreement parent uses this when a busy child does not handle LanguageChanged.
 */
export const AssignRequestedLeaseDocumentLanguage = assign((_context: any, event: any) => ({
  requestedLeaseDocumentsLanguage: event.data?.language,
}));

/**
 * Neither machine ever resets these — every write is an event payload — which was harmless while
 * generation ran once per entry. Now that a toggle can re-run it, a failure in one language would
 * otherwise strand the page on InternalError for good: the toggle lives in the footer and stays
 * clickable, so the customer would keep regenerating behind an error screen they can never leave.
 */
const clearedErrors = { generateContractErrors: [], signingErrors: [] };

/**
 * Register on both `waiting` and `callingGenerateContract` — on the latter so a second toggle
 * mid-request re-enters the state, cancelling the in-flight call. An event without a language
 * matches neither branch and is ignored.
 */
export const createLanguageChangedTransitions = () => [
  {
    cond: (context: any, event: any) => !!context.leaseDocumentsByLanguage?.[event.data?.language as SupportedLanguage],
    target: 'waiting',
    actions: assign((context: any, event: any) => ({
      requestedLeaseDocumentsLanguage: event.data?.language,
      leaseDocumentsLanguage: event.data?.language,
      leaseDocuments: context.leaseDocumentsByLanguage?.[event.data?.language as SupportedLanguage],
      ...clearedErrors,
    })),
  },
  {
    cond: (_context: any, event: any) => !!event.data?.language,
    target: 'callingGenerateContract',
    actions: assign((_context: any, event: any) => ({
      requestedLeaseDocumentsLanguage: event.data?.language,
      leaseDocumentsLanguage: event.data?.language,
      ...clearedErrors,
    })),
  },
];

/**
 * Reconciles a language request recorded by the LeaseAgreement parent while another child
 * state was busy. This is evaluated whenever `waiting` is entered.
 */
export const createLeaseDocumentLanguageReconciliation = () => [
  {
    cond: (context: any) =>
      !!context.requestedLeaseDocumentsLanguage &&
      context.requestedLeaseDocumentsLanguage !== context.leaseDocumentsLanguage &&
      !!context.leaseDocumentsByLanguage?.[context.requestedLeaseDocumentsLanguage as SupportedLanguage],
    target: 'waiting',
    actions: assign((context: any) => ({
      leaseDocumentsLanguage: context.requestedLeaseDocumentsLanguage,
      leaseDocuments: context.leaseDocumentsByLanguage?.[context.requestedLeaseDocumentsLanguage as SupportedLanguage],
      ...clearedErrors,
    })),
  },
  {
    cond: (context: any) => !!context.requestedLeaseDocumentsLanguage && context.requestedLeaseDocumentsLanguage !== context.leaseDocumentsLanguage,
    target: 'callingGenerateContract',
    actions: assign((context: any) => ({
      leaseDocumentsLanguage: context.requestedLeaseDocumentsLanguage,
      ...clearedErrors,
    })),
  },
];
